import { GetBasicProjectListQuery, GetBasicProjectListQueryVariables, GetProjectListDocument, Order_By } from "@/graphql";
import gaiupApolloClient from "@/graphql/services/ApolloClient";
import { isStoreResetError } from "@/graphql/services/isStoreResetError";
import { retryAfterStoreReset } from "@/graphql/services/retryAfterStoreReset";
import debounce from "@/services/Utils/debounceNew";
import { useAuthStateStore } from "@/stores/Utils/auth/AuthState";
import * as Sentry from "@sentry/vue";
import { ref } from "vue";
import { ProjectListFetchParams } from "../types/Project";
import getProjectQueryWhere from "../utils/getProjectQueryWhere";

export const useBasicProjectList = () => {
  const authState = useAuthStateStore();
  const basicProjects = ref<GetBasicProjectListQuery["project"]>([]);
  const basicProjectListLoading = ref<boolean>(true);

  const fetchImmediate = async ({
    filters,
    hasAccessAllProjects,
    orderBy = [{ favorite: Order_By.desc_nulls_last }, { name: Order_By.asc_nulls_last }],
    searchString,
  }: ProjectListFetchParams) => {
    basicProjectListLoading.value = true;

    try {
      // Use Apollo client directly since Vue Apollo query becomes stale after navigation
      const result = await retryAfterStoreReset(() =>
        gaiupApolloClient.query<GetBasicProjectListQuery, GetBasicProjectListQueryVariables>({
          query: GetProjectListDocument,
          fetchPolicy: "network-only",
          context: hasAccessAllProjects
            ? {
                headers: {
                  "x-hasura-role": "accessAllProjects",
                },
              }
            : undefined,
          variables: {
            orderBy,
            ...getProjectQueryWhere(filters, searchString, authState.user?.tenantId),
          },
        })
      );

      if (result?.data) {
        basicProjects.value = result.data.project;
      }
    } catch (error) {
      // A tenant switch resets the store mid-request and refetches the list itself.
      if (!isStoreResetError(error)) Sentry.captureException(error);
    }

    basicProjectListLoading.value = false;
  };

  const debouncedFetchImmediate = debounce((params: ProjectListFetchParams) => {
    fetchImmediate(params);
  }, 500);

  const fetchBasicProjects = (params: ProjectListFetchParams) => {
    if (params.immediately) {
      fetchImmediate(params);
      return;
    }

    debouncedFetchImmediate(params);
  };

  const upsertBasicProject = (project: GetBasicProjectListQuery["project"][0]) => {
    const index = basicProjects.value.findIndex((p) => p.id === project.id);
    if (index === -1) {
      basicProjects.value = [project, ...basicProjects.value];
    } else {
      // Update existing project
      const updatedProjects = [...basicProjects.value];
      updatedProjects[index] = project;
      basicProjects.value = [...updatedProjects];
    }
  };

  return {
    fetchBasicProjects,
    basicProjects,
    basicProjectListLoading,
    upsertBasicProject,
  };
};
